import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { TypedMemo } from '../../../common/types';
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton';
import { useCombinedPlaylistReach } from '../../hooks/useCombinedPlaylistReach';
import { useChartTokens } from '../../theme/useChartTokens';
import { PercentageChangeElement } from '../PositionChangeElement';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';
import styles from './CombinedPlaylistReachPanel.module.scss';

interface CombinedPlaylistReachPanelProps {
  endDate?: Date;
  days?: number;
}

const formatAxisValue = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

const CombinedPlaylistReachPanelComponent: React.FC<CombinedPlaylistReachPanelProps> = ({ endDate, days = 14 }) => {
  const intl = useIntl();
  const chart = useChartTokens();
  const { data: timeSeries, isLoading, error } = useCombinedPlaylistReach({ days, endDate });

  const data = useMemo(() => timeSeries?.data ?? [], [timeSeries?.data]);

  const last = data.length > 0 ? data[data.length - 1]!.count : 0;
  const first = data.length > 0 ? data[0]!.count : 0;
  const pctChange = first > 0 ? ((last - first) / first) * 100 : null;

  const dateRangeText = useMemo(() => {
    if (data.length < 2) {
      return '';
    }
    const start = new Date(data[0]!.date).toLocaleDateString(intl.locale, { month: 'short', day: 'numeric' });
    const end = new Date(data[data.length - 1]!.date).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${start} – ${end}`;
  }, [data, intl.locale]);

  const formatDate = (dateString: string, withYear = false): string =>
    new Date(dateString).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
    });

  return (
    <Panel className="h-100">
      <PanelHead
        titleAs="h2"
        icon={<FontAwesomeIcon icon={faChartLine} className={styles.icon} />}
        title={intl.formatMessage({
          id: 'app.sml.home.combinedReach.title',
          defaultMessage: 'Combined playlist reach',
        })}
        right={dateRangeText !== '' ? <span className={styles.meta}>{dateRangeText}</span> : null}
      />
      <PanelBody>
        <div className={styles.figureRow}>
          <span className={styles.figure}>{last.toLocaleString(intl.locale)}</span>
          <PercentageChangeElement percentageChange={pctChange} />
          <span className={styles.tag}>
            {intl.formatMessage({ id: 'app.sml.home.combinedReach.tag', defaultMessage: 'followers reached' })}
          </span>
        </div>

        {isLoading ? (
          <div style={{ height: '236px' }}>
            <ChartSkeleton />
          </div>
        ) : error !== null && error !== undefined ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '236px', color: chart.tx2 }}
          >
            {intl.formatMessage({ id: 'app.sml.chart.error', defaultMessage: 'Error loading chart data' })}
          </div>
        ) : data.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '236px', color: chart.tx2 }}
          >
            {intl.formatMessage({ id: 'app.sml.chart.no-data', defaultMessage: 'No data available for this period' })}
          </div>
        ) : (
          <div style={{ height: '236px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <defs>
                  <linearGradient id="combinedReachGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chart.brand} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={chart.brand} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: chart.label }}
                  tickFormatter={(d: string) => formatDate(d)}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: chart.axis }}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                  tickFormatter={formatAxisValue}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: chart.tooltipBg,
                    border: `1px solid ${chart.line}`,
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: chart.tooltipTx,
                  }}
                  labelFormatter={(d: string) => formatDate(d, true)}
                  formatter={(value: number) => [value.toLocaleString(), 'Followers reached']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={chart.brandLift}
                  strokeWidth={2}
                  fill="url(#combinedReachGradient)"
                  dot={{ fill: chart.brandLift, strokeWidth: 1, r: 3 }}
                  activeDot={{ r: 5, stroke: chart.brandLift, strokeWidth: 1, fill: chart.dotCore }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const CombinedPlaylistReachPanel = TypedMemo(CombinedPlaylistReachPanelComponent);
